import { useCallback, useEffect, useRef, useState } from "react";
import { Task, TaskFilter } from "../api/Tasks";
import { useTaskFeed } from "./Feed";
import { useSyncedRef } from "./SyncedRef";

// pageSize is how much of a list is read at a time
export const pageSize = 25;

type Page = {
    tasks: Task[];
    total: number;
}

// useTaskPages reads a list a page at a time. A page carries on from the last task of
// the page before it, and is watched on its own, so every page that has been loaded
// keeps up with what changes in it.
//
// A page is anchored to the row it starts after, which is what a task added or ticked
// off above that row moves: the row either shows twice or drops out of the list until
// the paging starts over. The rows are handed back by id once each, so only the gap
// is left to see.
//
// A null filter reads nothing, which is how a list that hasn't been asked for yet
// costs neither a query nor a subscription.
export function useTaskPages(filter: TaskFilter | null) {
    // one cursor per page, in the order they were loaded. The first page starts at the
    // top of the list, which is what an id of 0 names.
    const [cursors, setCursors] = useState<number[]>([0]);
    const [pages, setPages] = useState<Record<number, Page>>({});

    // a change of what's being listed starts the paging over
    const key = JSON.stringify(filter);
    const listed = useRef(key);
    useEffect(() => {
        if (listed.current === key) return;

        listed.current = key;
        setCursors([0]);
        setPages({});
    }, [key]);

    const onPage = useCallback((cursor: number, page: Page) => {
        setPages(old => ({ ...old, [cursor]: page }));
    }, []);

    // a row that has moved across a page boundary since its page was read is in two
    // of them, and belongs in the list once
    const seen = new Set<number>();
    const tasks: Task[] = [];

    for (const cursor of cursors) {
        const page = pages[cursor];
        if (!page) continue;

        for (const task of page.tasks) {
            if (seen.has(task.id)) continue;

            seen.add(task.id);
            tasks.push(task);
        }
    }

    const first = pages[0];
    const last = pages[cursors[cursors.length - 1]];

    // only the first page is counted, so it's what says how much of the list is still
    // to come. A page that came back empty is the end of it either way.
    const total = first ? first.total : 0;
    const more = !!last && last.tasks.length > 0 && tasks.length < total;

    const loadMore = () => {
        const end = tasks[tasks.length - 1];
        if (!end) return;

        setCursors(old => old.includes(end.id) ? old : old.concat(end.id));
    };

    // each page needs a component of its own: a hook can't be called once per page of
    // a list that grows
    const element = filter
        ? cursors.map(cursor => (
            <TaskPage
                key={cursor}
                cursor={cursor}
                filter={{ ...filter, limit: pageSize, after: cursor || undefined }}
                onChange={onPage}
            />
        ))
        : null;

    return {
        tasks: tasks,
        total: total,
        loading: !!filter && !first,
        more: more,
        loadMore: loadMore,

        // element watches the pages and draws whatever loading or error state they're
        // in, so it belongs under the list it fills
        element: element,
    };
}

// TaskPage watches one page and hands it to whoever owns the whole list
function TaskPage(props: {
    cursor: number;
    filter: TaskFilter;
    onChange: (cursor: number, page: Page) => void;
}) {
    const list = useTaskFeed(props.filter);
    const onChange = useSyncedRef(props.onChange);
    const cursor = props.cursor;

    useEffect(() => {
        onChange.current(cursor, { tasks: list.tasks, total: list.total });
    }, [cursor, list.tasks, list.total, onChange]);

    return <>{list.LoadingOrErrorElement}</>;
}
